import type { DeclarationDts } from '@shared/types/Dts';
import type { Tenant } from '@shared/types/Tenant';
import { libelleTrimestre, totauxVentilationDts } from '@domain/fiscalite/DtsService';
import { fmtNumber, esc } from '@presentation/shell/format';
import { nxBannerLeftHtml } from '@presentation/print/documentBrand';

export interface DtsDocument {
  title: string;
  filename: string;
  html: string;
}

const STATUT_LIBELLE: Record<DeclarationDts['statut'], string> = {
  brouillon: 'Brouillon',
  deposee: 'Déposée',
  annulee: 'Annulée',
};

/** Ventilation de la CNSS patronale du trimestre (communiqué CNSS du 12/01/2026) : total, part patronale et part salariale séparés. */
function ventilationHtml(dts: DeclarationDts): string {
  if (dts.lignes.length === 0) return '';
  const t = totauxVentilationDts(dts.lignes);
  return `<p style="margin-top:14px; font-weight:700; color:var(--nx-marine);">Ventilation des cotisations CNSS:</p>
        <table class="nx-amounts">
          <thead><tr><th>Branche</th><th class="num">Montant (FCFA)</th></tr></thead>
          <tbody>
            <tr><td>Prestations familiales et maternité — employeur</td><td class="num">${fmtNumber(t.pf)}</td></tr>
            <tr><td>Accidents du travail et maladies professionnelles — employeur</td><td class="num">${fmtNumber(t.atmp)}</td></tr>
            <tr><td>Assurance vieillesse, invalidité et décès (VID) — employeur</td><td class="num">${fmtNumber(t.vid)}</td></tr>
            <tr><td><strong>Part patronale</strong></td><td class="num"><strong>${fmtNumber(dts.total_patronale)}</strong></td></tr>
            <tr><td>Part salariale (VID)</td><td class="num">${fmtNumber(dts.total_salariale)}</td></tr>
            <tr class="nx-total"><td><strong>Total CNSS</strong></td><td class="num"><strong>${fmtNumber(dts.total_patronale + dts.total_salariale)}</strong></td></tr>
          </tbody>
        </table>`;
}

/**
 * Document imprimable de la DTS (Déclaration Trimestrielle des Salaires),
 * à transmettre à la CNSS. Charte documentaire `nx-*` (DAS 1 / attestations).
 */
export function buildDtsHTML(dts: DeclarationDts, t: Tenant | null): DtsDocument {
  const today = new Date().toLocaleDateString('fr-FR');
  const ville = t?.ville || 'Libreville';
  const refDoc = dts.reference_depot || `DTS-${dts.trimestre}`;
  const libelle = libelleTrimestre(dts.trimestre);

  const corps = dts.lignes
    .map(
      (l) => `<tr>
        <td style="font-family:'Courier New',monospace;">${esc(l.matricule)}</td>
        <td>${esc(l.nom_complet)}</td>
        <td style="font-family:'Courier New',monospace;">${esc(l.numero_cnss || '—')}</td>
        <td class="num">${fmtNumber(l.brut)}</td>
        <td class="num">${fmtNumber(l.base_cnss)}</td>
        <td class="num">${fmtNumber(l.patronale)}</td>
        <td class="num">${fmtNumber(l.salariale)}</td>
        <td class="num">${l.mois_presents}</td>
      </tr>`,
    )
    .join('');

  const html = `<div class="nx-attestation nx-salaire" id="nx-dts-print">
    <div class="nx-banner">
      ${nxBannerLeftHtml()}
      <div class="nx-banner-right">
        <div class="nx-co">${esc(t?.nom || 'Entreprise')}</div>
        <div class="nx-co-meta">${esc(t?.adresse || '')}<br>${esc(t?.ville || '')}${t?.telephone ? ' · ' + esc(t.telephone) : ''}<br>NIF ${esc(t?.nif || '—')} · RCCM ${esc(t?.rccm || '—')}</div>
      </div>
    </div>
    <div class="nx-doc-ref"><div>Réf. document <span class="nx-ref-num">${esc(refDoc)}</span></div><div>Édité le ${esc(today)}</div></div>
    <div class="nx-body">
      <h1 class="nx-title">DÉCLARATION TRIMESTRIELLE DES SALAIRES</h1>
      <div class="nx-subtitle">DTS — ${esc(libelle)} · ${esc(STATUT_LIBELLE[dts.statut])}</div>
      <div class="nx-content">
        <p>La société <strong>${esc(t?.nom || 'la société')}</strong>, immatriculée au NIF n° <strong>${esc(t?.nif || '—')}</strong>, déclare les rémunérations versées à son personnel au titre du trimestre <strong>${esc(libelle)}</strong>, ainsi que les cotisations CNSS afférentes.</p>
        <div class="nx-highlight-cards">
          <div class="nx-highlight-card"><div class="nx-hc-label">Effectif déclaré</div><div class="nx-hc-value">${dts.effectif}</div><div class="nx-hc-sub">salarié(s)</div></div>
          <div class="nx-highlight-card cyan"><div class="nx-hc-label">Brut du trimestre</div><div class="nx-hc-value">${fmtNumber(dts.total_brut)}</div><div class="nx-hc-sub">FCFA</div></div>
          <div class="nx-highlight-card menthe"><div class="nx-hc-label">Cotisations CNSS</div><div class="nx-hc-value">${fmtNumber(dts.total_patronale + dts.total_salariale)}</div><div class="nx-hc-sub">FCFA</div></div>
        </div>
        <p style="margin-top:18px; font-weight:700; color:var(--nx-marine);">État nominatif des rémunérations:</p>
        <table class="nx-amounts">
          <thead><tr><th>Matricule</th><th>Salarié</th><th>N° CNSS</th><th class="num">Brut</th><th class="num">Base plaf.</th><th class="num">Patronale</th><th class="num">Salariale</th><th class="num">Mois</th></tr></thead>
          <tbody>
            ${corps || '<tr><td colspan="8">Aucun salarié rémunéré sur le trimestre.</td></tr>'}
            <tr class="nx-total"><td colspan="3"><strong>TOTAUX</strong></td><td class="num"><strong>${fmtNumber(dts.total_brut)}</strong></td><td class="num"><strong>${fmtNumber(dts.total_base_cnss)}</strong></td><td class="num"><strong>${fmtNumber(dts.total_patronale)}</strong></td><td class="num"><strong>${fmtNumber(dts.total_salariale)}</strong></td><td></td></tr>
          </tbody>
        </table>
        ${ventilationHtml(dts)}
        <div class="nx-legal-mention"><strong>Obligation déclarative:</strong> la DTS est déposée auprès de la Caisse Nationale de Sécurité Sociale (CNSS). Montants exprimés en FCFA. Périodes couvertes : ${esc(dts.periodes.join(', '))}.</div>
      </div>
      <div class="nx-signature"><div class="nx-date">Fait à <strong>${esc(ville)}</strong>,<br>le <strong>${esc(today)}</strong></div><div class="nx-sign-block"><div class="nx-sign-line">La Direction</div><div class="nx-sign-role">Représentant légal</div></div></div>
    </div>
    <div class="nx-footer">
      <div><span class="nx-foot-co">${esc(t?.nom || '')}</span> &nbsp;·&nbsp; ${esc(t?.email || '')} &nbsp;·&nbsp; ${esc(t?.telephone || '')}</div>
      <div>Page 1/1 &nbsp;·&nbsp; Doc. ${esc(refDoc)}</div>
    </div>
  </div>`;

  return { title: `DTS — ${libelle}`, filename: `dts_${dts.trimestre}.pdf`, html };
}
